import { Button, Stack, Tab, Tabs, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import CancelFiltersButton from '../../../components/common/CancelFiltersButton'
import CustomContainer from '../../../components/common/CustomContainer'
import DateRangePicker from '../../../components/common/DateRangePicker'
import LinkWrapper from '../../../components/common/LinkWrapper'
import OrderStateFilter from '../../../components/common/OrderStateFilter'
import PaymentStateFilter from '../../../components/common/PaymentStateFilter'
import TabPanel from '../../../components/common/TabPanel'
import TextSearchField from '../../../components/common/TextSearchField'
import ForceApprovedEventsSheetSyncDialog from '../../../components/events/ForceApprovedEventsSheetSyncDialog'
import ScreenLoadingPlaceholder from '../../../components/ScreenLoadingPlaceholder'
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks'
import { RouteConstants } from '../../../navigation/navigation-types'
import { EventManager } from '../../../services/api/EventManager'
import { OrderState, PaymentState } from '../../../services/api/sharedTypes/Common'
import { EventBriefDto, EventInPlanningDto } from '../../../services/api/sharedTypes/Event'
import { UserRole } from '../../../services/api/sharedTypes/User'
import { getAuthState } from '../../../services/redux/auth/auth-selectors'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { convertNumberToEventTab, EventTab, getEventTab, getEventTabNumber } from '../../../utils/event-utils'
import { convertToSearchString, openInNewTab } from '../../../utils/global-utils'
import EventPlanning from '../planning/EventPlanning'
import EventTable from './EventTable'

interface eventFilter {
    dateFrom: Date | null
    dateTo: Date | null
    searchedValue: string
    selectedOrderState: OrderState[]
    selectedPaymentState: PaymentState[]
}

const date = new Date()
const today = new Date(date.getFullYear(), date.getMonth(), date.getDate())
const todayPlus14Days = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 14)

const Events: React.FC = () => {
    const dispatch = useAppDispatch()

    const { role: userRole } = useAppSelector(getAuthState)

    const isAdmin = userRole === UserRole.Admin

    const [tableFilters, setTableFilters] = useState<eventFilter>({
        dateFrom: null,
        dateTo: null,
        searchedValue: '',
        selectedOrderState: [],
        selectedPaymentState: [],
    })

    const [planningFilters, setPlanningFilters] = useState<eventFilter>({
        dateFrom: today,
        dateTo: todayPlus14Days,
        searchedValue: '',
        selectedOrderState: [],
        selectedPaymentState: [],
    })

    const [selectedTabValue, setSelectedTabValue] = useState<number>(
        !isAdmin ? getEventTabNumber(EventTab.Planning) : getEventTabNumber(getEventTab(location.hash.replace('#', ''))) ?? 0
    )
    const [loadingDateFilter, setLoadingDateFilter] = useState<boolean>(false)
    const [events, setEvents] = useState<EventBriefDto[]>([])
    const [eventsForPlanning, setEventsForPlanning] = useState<EventInPlanningDto[]>([])
    const [forceSheetSyncOpen, setForceSheetSyncOpen] = useState(false)

    const handleChangeTabValue = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, newValue: number) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (event.metaKey || event.button === 1 || event.ctrlKey) {
            openInNewTab(RouteConstants.events + '#' + convertNumberToEventTab(newValue))
        } else {
            setSelectedTabValue(newValue)
            location.hash = convertNumberToEventTab(newValue)
        }
    }

    const getFilter = () => {
        if (selectedTabValue === 0) return tableFilters
        else return planningFilters
    }

    const setFilter = (filter: eventFilter) => {
        if (selectedTabValue === 0) setTableFilters(filter)
        else setPlanningFilters(filter)
    }

    const onChangePaymentStateFilter = (paymentState: PaymentState[]) => {
        setFilter({
            ...getFilter(),
            selectedPaymentState: paymentState,
        })
    }

    const onChangeOrderStateFilter = (orderState: OrderState[]) => {
        setFilter({
            ...getFilter(),
            selectedOrderState: orderState,
        })
    }

    const removeEvent = async (id: string) => {
        if (!events) return

        try {
            await EventManager.getManager().deleteEventAsync(id)
            const newEvents = events.filter((event) => event.id !== id)
            setEvents(newEvents)
        } catch (error) {
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        dispatch(displayMessage({ message: 'Událost smazána', type: 'success' }))
    }

    const getFilteredEventsByDate = async () => {
        if (!events) return

        setLoadingDateFilter(true)

        const { dateFrom, dateTo } = getFilter()

        try {
            if (selectedTabValue === 0 && isAdmin) {
                const eventRes = await EventManager.getManager().getEventListAsync(dateFrom ?? undefined, dateTo ?? undefined)
                if (eventRes) {
                    setEvents(eventRes.events)
                }
            } else {
                const eventRes = await EventManager.getManager().getEventListForPlanningAsync(
                    dateFrom ?? undefined,
                    dateTo ?? undefined
                )
                if (eventRes) {
                    setEventsForPlanning(eventRes.events)
                }
            }
        } catch (error) {
            setLoadingDateFilter(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }

        setLoadingDateFilter(false)
    }

    useEffect(() => {
        if (getEventTabNumber(EventTab.Planning) && !isAdmin)
            setSelectedTabValue(getEventTabNumber(EventTab.Planning))

        getFilteredEventsByDate()
    }, [selectedTabValue, getFilter().dateFrom, getFilter().dateTo])

    const eventsForTable = events
        ?.filter((event) => {
            if (getFilter().searchedValue) {
                return (
                    convertToSearchString(event.name).includes(convertToSearchString(getFilter().searchedValue)) ||
                    convertToSearchString(event.address.city).includes(convertToSearchString(getFilter().searchedValue)) ||
                    convertToSearchString(event.address.street).includes(convertToSearchString(getFilter().searchedValue)) ||
                    convertToSearchString(event.address.zip).includes(convertToSearchString(getFilter().searchedValue)) ||
                    convertToSearchString(event.clientName).includes(convertToSearchString(getFilter().searchedValue)) ||
                    convertToSearchString(event.clientLastName ?? '').includes(convertToSearchString(getFilter().searchedValue)) ||
                    convertToSearchString(event.description).includes(convertToSearchString(getFilter().searchedValue))
                )
            }
            return true
        })
        .filter((event) => {
            return getFilter().selectedOrderState.length === 0 || getFilter().selectedOrderState.includes(event.orderState)
        })
        .filter((event) => {
            return (
                getFilter().selectedPaymentState.length === 0 ||
                (event.paymentState === PaymentState.PaidWithoutInvoice
                    ? getFilter().selectedPaymentState.includes(PaymentState.Paid)
                    : getFilter().selectedPaymentState.includes(event.paymentState))
            )
        })

    const filteredEventsForPlanningDetail = eventsForPlanning?.filter((event) => {
        if (getFilter().searchedValue) {
            return (
                convertToSearchString(event.name).includes(convertToSearchString(getFilter().searchedValue)) ||
                convertToSearchString(event.planningNote).includes(convertToSearchString(getFilter().searchedValue)) ||
                convertToSearchString(event.address.city).includes(convertToSearchString(getFilter().searchedValue)) ||
                convertToSearchString(event.address.street).includes(convertToSearchString(getFilter().searchedValue)) ||
                convertToSearchString(event.address.zip).includes(convertToSearchString(getFilter().searchedValue))
            )
        }
        return true
    }).filter((event) => {
        return getFilter().selectedOrderState.length === 0 || getFilter().selectedOrderState.includes(event.orderState)
    }).filter((event) => {
        return (
            getFilter().selectedPaymentState.length === 0 ||
            (event.paymentState === PaymentState.PaidWithoutInvoice
                ? getFilter().selectedPaymentState.includes(PaymentState.Paid)
                : getFilter().selectedPaymentState.includes(event.paymentState))
        )
    })

    return (
        <CustomContainer>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='h5'>Události</Typography>
                {
                    isAdmin && (
                        <Stack direction='row' spacing={1}>
                            <Button variant='outlined' onClick={() => setForceSheetSyncOpen(true)}>
                                Aktualizovat rozpis akcí
                            </Button>
                            <LinkWrapper route={RouteConstants.addEvent}>
                                <Button variant='contained'>+ Přidat událost</Button>
                            </LinkWrapper>
                        </Stack>
                    )
                }
            </Stack>
            {isAdmin && (
                <ForceApprovedEventsSheetSyncDialog
                    open={forceSheetSyncOpen}
                    onClose={() => setForceSheetSyncOpen(false)}
                />
            )}

            {
                isAdmin && (
                    <Tabs value={selectedTabValue}>
                        <Tab label='Tabulka' onMouseDown={(e) => handleChangeTabValue(e, getEventTabNumber(EventTab.Table))} />
                        <Tab label='Plánování' onMouseDown={(e) => handleChangeTabValue(e, getEventTabNumber(EventTab.Planning))} />
                    </Tabs>
                )
            }

            <Stack direction='row' spacing={3} mt={1} mb={3} overflow='hidden'>
                <OrderStateFilter orderState={
                    getFilter().selectedOrderState
                } onChange={onChangeOrderStateFilter} />

                <PaymentStateFilter paymentState={
                    getFilter().selectedPaymentState
                } onChange={onChangePaymentStateFilter} />

                <DateRangePicker
                    labelFrom='Události od'
                    labelTo='Události do'
                    dateTo={getFilter().dateTo}
                    dateFrom={getFilter().dateFrom}
                    onSetDateFrom={
                        (date) =>
                            setFilter({
                                ...getFilter(),
                                dateFrom: date,
                            })

                    }
                    onSetDateTo={
                        (date) =>
                            setFilter({
                                ...getFilter(),
                                dateTo: date,
                            })

                    }
                />
                <TextSearchField value={
                    getFilter().searchedValue ?? ''
                } onChange={(e) => {
                    setFilter({
                        ...getFilter(),
                        searchedValue: e.target.value,
                    })
                }} />

                {
                    getFilter().dateFrom || getFilter().dateTo || getFilter().searchedValue || getFilter().selectedOrderState.length > 0 || getFilter().selectedPaymentState.length > 0 ? (

                        <CancelFiltersButton onClick={() => setFilter(
                            {
                                dateFrom: null,
                                dateTo: null,
                                searchedValue: '',
                                selectedOrderState: [],
                                selectedPaymentState: [],
                            }
                        )} />
                    ) : null
                }

            </Stack>

            {loadingDateFilter ? (
                <ScreenLoadingPlaceholder />
            ) : (
                <>
                    <TabPanel value={selectedTabValue} index={getEventTabNumber(EventTab.Table)}>
                        {events ? (
                            <EventTable events={eventsForTable ?? events} onDelete={removeEvent} />
                        ) : (
                            <Typography variant='body1'>Nejsou zde žádné události</Typography>
                        )}
                    </TabPanel>
                    <TabPanel value={selectedTabValue} index={getEventTabNumber(EventTab.Planning)}>
                        {filteredEventsForPlanningDetail ? (
                            <>
                                {filteredEventsForPlanningDetail.length < 1 && (
                                    <Typography variant='h6' m={3}>
                                        {getFilter().dateFrom && getFilter().dateTo
                                            ? 'Nejsou zde žádné události v tomto rozmezí'
                                            : 'Pro zobrazení výsledku nejprve zvolte období'}
                                    </Typography>
                                )}

                                <EventPlanning
                                    onRefetchEvents={() => getFilteredEventsByDate()}
                                    events={filteredEventsForPlanningDetail}
                                    onChange={setEventsForPlanning}
                                    userIsAdmin={isAdmin}
                                />
                            </>
                        ) : (
                            <Typography variant='h6' m={3}>
                                Nejsou zde žádné události
                            </Typography>
                        )}
                    </TabPanel>
                </>
            )}
        </CustomContainer>
    )
}

export default Events
